// Proving Ground — the 24/7 bot's LIVE paper-trading record. Every strategy
// simulates trades against real prices in the backend; only strategies that
// prove out here are trusted with real money (accreditation).
import { useEffect, useState, useCallback } from "react";
import { FlaskConical, RefreshCw, ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { getAdminToken, authHeader } from "@/lib/adminAuth";
import { toast } from "sonner";

const fmtT = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour12: false }); } catch { return "—"; } };

const PaperTradesPanel = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/risefall-bot/paper-trades", { params: { limit: 30 } });
      setData(res.data);
    } catch { /* panel is informational — stay quiet */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchData();
    const t = setInterval(fetchData, 30000);
    return () => clearInterval(t);
  }, [open, fetchData]);

  const minN = data?.accreditation?.min_trades ?? 20;
  const minWr = data?.accreditation?.min_wr ?? 58;

  return (
    <div data-testid="paper-trades-panel" className="mb-3 rounded-lg border border-amber-400/25 bg-amber-400/5 p-2.5">
      <button type="button" data-testid="paper-trades-toggle" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 text-left">
        {open ? <ChevronDown className="h-3.5 w-3.5 text-amber-300" /> : <ChevronRight className="h-3.5 w-3.5 text-amber-300" />}
        <FlaskConical className="h-3.5 w-3.5 text-amber-300" />
        <span className="text-[11px] font-semibold text-white">Proving Ground — live simulated trades</span>
        <span className="text-[10px] text-vts-silver/55 font-mono">same entry rules as the real bot (incl. M5 trend confirm) · full stake: {minN} sims ≥60% or 100 sims ≥58% · 🟡 half stake: {data?.accreditation?.provisional_trades ?? 15}–29 sims ≥{minWr}%</span>
        {open && (
          <span className="ml-auto flex items-center gap-2">
            {!!getAdminToken() && (
              <Trash2
                data-testid="paper-trades-reset-btn"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!window.confirm("Reset ALL simulated trades? Accreditation rebuilds from scratch — the bot pauses real entries until strategies re-qualify.")) return;
                  try {
                    const res = await api.post("/risefall-bot/paper-trades/reset", {}, { headers: authHeader() });
                    toast.success(`Proving Ground reset — ${res.data.deleted} sims cleared`);
                    fetchData();
                  } catch {
                    toast.error("Reset failed — admin login required");
                  }
                }}
                className="h-3.5 w-3.5 text-vts-loss/70 hover:text-vts-loss cursor-pointer"
              />
            )}
            <RefreshCw onClick={(e) => { e.stopPropagation(); fetchData(); }}
              className={`h-3.5 w-3.5 text-vts-silver/60 hover:text-white ${loading ? "animate-spin" : ""}`} />
          </span>
        )}
      </button>
      {open && data && (
        <div className="mt-2 space-y-3">
          {/* Duration shoot-out: SAME signals simulated at 2 min AND 3 min */}
          {(data.durations || []).length > 0 && (
            <div data-testid="paper-duration-compare">
              <p className="text-[9px] uppercase tracking-wider text-vts-silver/45 font-mono mb-1">
                Duration shoot-out — identical entries, different expiry (est. net per $1 at ~85% payout)
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {data.durations.map((d) => {
                  const metric = (z) => (z.secure_n ? z.est_secure : z.est_net);
                  const best = data.durations.length > 1 && metric(d) === Math.max(...data.durations.map(metric));
                  const acc = (data.durations_accredited || []).find((z) => z.duration_min === d.duration_min);
                  return (
                    <div key={d.duration_min} data-testid={`paper-duration-${d.duration_min}m`}
                      className={`rounded-md border px-2.5 py-2 ${best ? "border-vts-profit/50 bg-vts-profit/5" : "border-vts-line bg-vts-bg/40"}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-mono text-sm font-semibold">{d.duration_min} min</span>
                        {d.duration_min === data.bot_duration && (
                          <span className="text-[8px] px-1 py-0.5 rounded bg-vts-blue/15 text-vts-blue border border-vts-blue/30 uppercase tracking-wider">bot uses this</span>
                        )}
                        {best && <span className="text-[8px] px-1 py-0.5 rounded bg-vts-profit/15 text-vts-profit border border-vts-profit/30 uppercase tracking-wider">leading</span>}
                      </div>
                      <div className="text-[10px] font-mono text-vts-silver/70 mt-1">
                        {d.n} sims · {d.win_rate}% win ·{" "}
                        <span className={d.est_net >= 0 ? "text-vts-profit" : "text-vts-loss"}>
                          flat {d.est_net >= 0 ? "+" : "−"}${Math.abs(d.est_net).toFixed(2)}
                        </span>
                      </div>
                      {d.secure_n > 0 && (
                        <div className="text-[10px] font-mono mt-0.5" data-testid={`paper-duration-${d.duration_min}m-secure`}>
                          <span className="text-amber-300/90">🔒 with Auto-Secure: </span>
                          <span className={d.est_secure >= 0 ? "text-vts-profit" : "text-vts-loss"}>
                            est {d.est_secure >= 0 ? "+" : "−"}${Math.abs(d.est_secure).toFixed(2)}
                          </span>
                          <span className="text-vts-silver/40"> ({d.secure_n} sims)</span>
                        </div>
                      )}
                      {acc && acc.n > 0 && (
                        <div className="text-[10px] font-mono mt-0.5" data-testid={`paper-duration-${d.duration_min}m-accredited`}>
                          <span className="text-vts-profit">✅ accredited only: </span>
                          <span className="text-vts-silver/70">{acc.n} sims · {acc.win_rate}% · </span>
                          <span className={(acc.secure_n ? acc.est_secure : acc.est_net) >= 0 ? "text-vts-profit" : "text-vts-loss"}>
                            est {(acc.secure_n ? acc.est_secure : acc.est_net) >= 0 ? "+" : "−"}$
                            {Math.abs(acc.secure_n ? acc.est_secure : acc.est_net).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-[9px] text-vts-silver/40 font-mono mt-1">
                Let this run ~1 day; if one duration clearly leads on 100+ sims, tell me and I'll switch the bot to it.
              </p>
            </div>
          )}
          <div data-testid="paper-trades-summary">
            <p className="text-[9px] uppercase tracking-wider text-vts-silver/45 font-mono mb-1">
              Accreditation progress (last {data.accreditation?.window_h ?? 48}h · scored on the {data.bot_duration ?? 3}-min sims the bot actually trades) · {data.open} simulations open now
            </p>
            {data.summary.length === 0 ? (
              <p className="text-[11px] text-vts-silver/50 font-mono">No settled paper trades yet — they appear ~4 min after the bot's first scan.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                {data.summary.map((s) => (
                  <div key={s.strategy_id} data-testid={`paper-summary-${s.strategy_id}`}
                    className="flex items-center gap-2 text-[10px] font-mono border-b border-vts-line/30 py-0.5">
                    <span className={s.accredited ? "text-vts-profit" : s.provisional ? "text-yellow-300" : "text-amber-300/90"}>{s.accredited ? "✅" : s.provisional ? "🟡" : "🧪"}</span>
                    <span className="text-white truncate flex-1">{s.strategy}</span>
                    <span className="text-vts-silver/60">{s.n}/{minN}</span>
                    <span className={s.accredited || s.provisional ? "text-vts-profit" : "text-vts-silver/50"}>{s.win_rate}%</span>
                    <div className="w-14 h-1 rounded bg-vts-line/60 overflow-hidden">
                      <div className={`h-full ${s.accredited ? "bg-vts-profit" : "bg-amber-400/70"}`}
                        style={{ width: `${Math.min(100, (s.n / minN) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div data-testid="paper-trades-recent">
            <p className="text-[9px] uppercase tracking-wider text-vts-silver/45 font-mono mb-1">Latest simulated trades</p>
            {data.recent.length === 0 ? (
              <p className="text-[11px] text-vts-silver/50 font-mono">Nothing recorded yet.</p>
            ) : (
              <div className="max-h-48 overflow-y-auto divide-y divide-vts-line/30">
                {data.recent.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-[10px] font-mono py-0.5">
                    <span className="text-vts-silver/50 w-14 shrink-0">{fmtT(t.created_at)}</span>
                    <span className="text-white truncate flex-1">{t.strategy}</span>
                    <span className="text-vts-silver/70 w-16 shrink-0">{t.symbol}</span>
                    <span className="text-vts-silver/50 w-7 shrink-0">{t.duration_min || 3}m</span>
                    <span className={t.side === "CALL" ? "text-vts-profit" : "text-vts-loss"}>{t.side === "CALL" ? "RISE" : "FALL"}</span>
                    <span className={`w-12 text-right shrink-0 ${t.status === "won" ? "text-vts-profit" : t.status === "lost" ? "text-vts-loss" : "text-amber-300/80"}`}>
                      {t.status === "open" ? "open" : t.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <p className="text-[9px] text-vts-silver/40 font-mono">
            Simulated only — no real money moves here. This forward record (not the backtest) decides which strategies the bot trusts.
          </p>
        </div>
      )}
    </div>
  );
};

export default PaperTradesPanel;
